import { Group, Mesh, Vector3, Euler, Box3, TextureLoader, MeshStandardMaterial } from 'three';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry.js';
import { Evaluator, INTERSECTION, Brush } from 'three-bvh-csg';
import * as THREE from 'three';

export interface StickerInfo {
  id: string;
  name: string;
  stickerMesh: Mesh;
  attachMesh: Mesh;
  position: Vector3;
  rotation: Euler;
  scale: Vector3;
  scaleRatio: number;
  renderOrder: number;
  isDualSided: boolean;
  userData: any;
}

export interface StickerViewerInfo {
  id: string;
  name: string;
  viewer: Group;
  scale: Vector3;
  rotation: Euler;
  position: Vector3;
  userData: any;
}

export interface DecalSettings {
  position: Vector3;
  rotation: Euler;
  scale: Vector3;
  depth: number;
  renderOrder?: number;
}

export class StickerManager {
  private _stickerViewer: Group | null = null;
  private _stickers: Map<string, StickerInfo> = new Map();
  private _stickerViewers: Map<string, StickerViewerInfo> = new Map();
  private _textureLoader: TextureLoader = new TextureLoader();
  private _nextStickerId: number = 1;
  private _nextViewerId: number = 1;

  // 콜백 함수들
  public _onStickerAdded: ((stickerInfo: StickerInfo) => void) | null = null;
  public _onStickerRemoved: ((stickerId: string) => void) | null = null;
  public _onStickerAttached: ((stickerId: string, attachMeshId: string) => void) | null = null;
  public _onViewerCreated: ((viewerInfo: StickerViewerInfo) => void) | null = null;
  public _onViewerRemoved: ((viewerId: string) => void) | null = null;

  constructor() {}

  /**
   * 스티커 뷰어 생성
   */
  public createStickerViewer(
    name?: string,
    position: Vector3 = new Vector3(0, 0, -1),
    userData?: any,
  ): string {
    const id = this.generateViewerId();
    const viewerName = name || `StickerViewer_${id}`;

    const viewer = new Group();
    viewer.name = viewerName;
    viewer.position.copy(position);

    const viewerInfo: StickerViewerInfo = {
      id,
      name: viewerName,
      viewer,
      scale: new Vector3(1, 1, 1),
      rotation: new Euler(0, 0, 0),
      position: position.clone(),
      userData: userData || {},
    };

    this._stickerViewers.set(id, viewerInfo);
    this._stickerViewer = viewer; // 메인 뷰어로 설정

    if (this._onViewerCreated) {
      this._onViewerCreated(viewerInfo);
    }

    return id;
  }

  /**
   * 스티커 뷰어에 이미지 추가
   */
  public async addStickerImage(
    viewerId: string,
    imageUrl: string,
    renderOrder: number = 0,
    size?: number,
  ): Promise<Mesh | null> {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo) return null;

    try {
      const texture = await this.loadTexture(imageUrl);
      const max = Math.max(texture.image.width, texture.image.height);

      // 스케일 설정
      const scale = size ? size / 3 : 3;
      viewerInfo.viewer.scale.set(scale, scale, scale);
      viewerInfo.scale.set(scale, scale, scale);

      // 머티리얼 생성
      const material = new MeshStandardMaterial({
        map: texture,
        roughness: 0.261,
        metalness: 0.507,
        opacity: 0.7,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        envMapIntensity: 1,
        wireframe: false,
      });

      // 기하학 생성
      const geometry = new THREE.PlaneGeometry(
        texture.image.width / max,
        texture.image.height / max,
        10,
        10,
      );

      const mesh = new Mesh(geometry, material);
      mesh.renderOrder = renderOrder;
      viewerInfo.viewer.add(mesh);

      return mesh;
    } catch (error) {
      console.error('스티커 이미지 로딩 실패:', error);
      return null;
    }
  }

  /**
   * 스티커 뷰어 스케일 변경
   */
  public setViewerScale(viewerId: string, scale: number): boolean {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo) return false;

    const newScale = scale / 3;
    viewerInfo.viewer.scale.set(newScale, newScale, viewerInfo.viewer.scale.z);
    viewerInfo.scale.set(newScale, newScale, viewerInfo.scale.z);

    return true;
  }

  /**
   * 스티커 뷰어 회전 변경
   */
  public setViewerRotation(viewerId: string, rotation: number): boolean {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo) return false;

    viewerInfo.viewer.rotation.z = rotation;
    viewerInfo.rotation.z = rotation;

    return true;
  }

  /**
   * 단면 스티커 부착
   */
  public attachOneSideSticker(
    viewerId: string,
    attachMesh: Mesh,
    intersectPoint: Vector3,
    renderOrder: number = 0,
    name?: string,
  ): StickerInfo | null {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo || !viewerInfo.viewer.children[0]) return null;

    const stickerMesh = viewerInfo.viewer.children[0] as Mesh;
    if (!stickerMesh.material || !(stickerMesh.material as MeshStandardMaterial).map) return null;

    const texture = (stickerMesh.material as MeshStandardMaterial).map!;
    const max = Math.max(texture.image.width, texture.image.height);
    const scaleRatio = this.calculateScaleRatio(viewerInfo.viewer, intersectPoint);

    // 데칼 기하학 생성
    const decalGeometry = new DecalGeometry(
      attachMesh,
      intersectPoint,
      viewerInfo.viewer.rotation,
      new Vector3(
        (texture.image.width / max) * viewerInfo.scale.x * scaleRatio,
        (texture.image.height / max) * viewerInfo.scale.y * scaleRatio,
        this.calculateDepth(attachMesh),
      ),
    );

    // 머티리얼 설정
    const material = (stickerMesh.material as MeshStandardMaterial).clone();
    material.depthTest = true;
    material.side = THREE.FrontSide;
    material.opacity = 1;
    material.needsUpdate = true;

    // 스티커 메시 생성
    const decalMesh = new Mesh(decalGeometry, material);
    const stickerName = name || `sticker_${this.generateStickerId()}`;
    decalMesh.name = stickerName;
    decalMesh.userData['isSticker'] = true;
    decalMesh.renderOrder = renderOrder;

    // 오브젝트에 스티커 추가
    attachMesh.add(decalMesh);

    // 스티커 정보 생성
    const stickerInfo: StickerInfo = {
      id: this.generateStickerId(),
      name: stickerName,
      stickerMesh: decalMesh,
      attachMesh,
      position: intersectPoint.clone(),
      rotation: viewerInfo.viewer.rotation.clone(),
      scale: viewerInfo.scale.clone(),
      scaleRatio,
      renderOrder,
      isDualSided: false,
      userData: {},
    };

    this._stickers.set(stickerInfo.id, stickerInfo);

    if (this._onStickerAdded) {
      this._onStickerAdded(stickerInfo);
    }

    if (this._onStickerAttached) {
      this._onStickerAttached(stickerInfo.id, attachMesh.name);
    }

    return stickerInfo;
  }

  /**
   * 양면 스티커 부착
   */
  public attachDualSideSticker(
    viewerId: string,
    attachMesh: Mesh,
    intersectPoint: Vector3,
    renderOrder: number = 0,
    name?: string,
  ): StickerInfo[] | null {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo || !viewerInfo.viewer.children[0]) return null;

    const stickerMesh = viewerInfo.viewer.children[0] as Mesh;
    if (!stickerMesh.material || !(stickerMesh.material as MeshStandardMaterial).map) return null;

    const texture = (stickerMesh.material as MeshStandardMaterial).map!;
    const max = Math.max(texture.image.width, texture.image.height);
    const scaleRatio = this.calculateScaleRatio(viewerInfo.viewer, intersectPoint);

    // 바운딩 박스 계산
    const boundingBox = new Box3().setFromObject(attachMesh);
    const center = new Vector3();
    boundingBox.getCenter(center);

    // 데칼 기하학 생성
    const decalGeometry = new DecalGeometry(
      attachMesh,
      intersectPoint,
      viewerInfo.viewer.rotation,
      new Vector3(
        (texture.image.width / max) * viewerInfo.scale.x * scaleRatio,
        (texture.image.height / max) * viewerInfo.scale.y * scaleRatio,
        100,
      ),
    );

    // 머티리얼 설정
    const material = (stickerMesh.material as MeshStandardMaterial).clone();
    material.depthTest = true;
    material.side = THREE.FrontSide;
    material.opacity = 1;
    material.needsUpdate = true;

    // CSG 연산을 위한 브러시 생성
    const evaluator = new Evaluator();
    const decalBrush = new Brush(decalGeometry);

    // 반쪽 박스 브러시 생성
    const size = boundingBox.getSize(new Vector3());
    const halfBoxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    halfBoxGeometry.translate(size.x / 2, center.y, center.z);
    const boxBrush = new Brush(halfBoxGeometry);

    // 교집합 연산
    const resultBrush = evaluator.evaluate(decalBrush, boxBrush, INTERSECTION);
    const resultMesh = new Mesh(resultBrush.geometry, material);

    const stickerName = name || `sticker_${this.generateStickerId()}`;
    resultMesh.name = stickerName;
    resultMesh.userData['isSticker'] = true;
    resultMesh.renderOrder = renderOrder;

    // 오브젝트에 스티커 추가
    attachMesh.add(resultMesh);

    // 스티커 정보 생성
    const stickerInfo: StickerInfo = {
      id: this.generateStickerId(),
      name: stickerName,
      stickerMesh: resultMesh,
      attachMesh,
      position: intersectPoint.clone(),
      rotation: viewerInfo.viewer.rotation.clone(),
      scale: viewerInfo.scale.clone(),
      scaleRatio,
      renderOrder,
      isDualSided: true,
      userData: {},
    };

    this._stickers.set(stickerInfo.id, stickerInfo);

    // 반대편 스티커 생성
    const mirroredMesh = resultMesh.clone();
    mirroredMesh.scale.x *= -1;
    mirroredMesh.name = `${stickerName}_mirrored`;
    mirroredMesh.userData['isSticker'] = true;
    mirroredMesh.renderOrder = renderOrder;

    attachMesh.add(mirroredMesh);

    const mirroredStickerInfo: StickerInfo = {
      id: this.generateStickerId(),
      name: `${stickerName}_mirrored`,
      stickerMesh: mirroredMesh,
      attachMesh,
      position: intersectPoint.clone(),
      rotation: viewerInfo.viewer.rotation.clone(),
      scale: viewerInfo.scale.clone(),
      scaleRatio,
      renderOrder,
      isDualSided: true,
      userData: {},
    };

    this._stickers.set(mirroredStickerInfo.id, mirroredStickerInfo);

    if (this._onStickerAdded) {
      this._onStickerAdded(stickerInfo);
      this._onStickerAdded(mirroredStickerInfo);
    }

    return [stickerInfo, mirroredStickerInfo];
  }

  /**
   * 스티커 제거
   */
  public removeSticker(stickerId: string): boolean {
    const stickerInfo = this._stickers.get(stickerId);
    if (!stickerInfo) return false;

    // 스티커 메시 제거
    if (stickerInfo.stickerMesh.parent) {
      stickerInfo.stickerMesh.parent.remove(stickerInfo.stickerMesh);
    }

    // 기하학과 머티리얼 정리
    stickerInfo.stickerMesh.geometry.dispose();
    if (stickerInfo.stickerMesh.material) {
      (stickerInfo.stickerMesh.material as THREE.Material).dispose();
    }

    // 스티커 정보 제거
    this._stickers.delete(stickerId);

    if (this._onStickerRemoved) {
      this._onStickerRemoved(stickerId);
    }

    return true;
  }

  /**
   * 모든 스티커 제거
   */
  public clearAllStickers(): void {
    const stickerIds = Array.from(this._stickers.keys());
    stickerIds.forEach((stickerId) => {
      this.removeSticker(stickerId);
    });
  }

  /**
   * 스티커 뷰어 제거
   */
  public removeStickerViewer(viewerId: string): boolean {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo) return false;

    // 뷰어의 모든 스티커 제거
    viewerInfo.viewer.children.forEach((child) => {
      if (child instanceof Mesh) {
        child.geometry.dispose();
        if (child.material) {
          (child.material as THREE.Material).dispose();
        }
      }
    });

    // 뷰어 제거
    if (viewerInfo.viewer.parent) {
      viewerInfo.viewer.parent.remove(viewerInfo.viewer);
    }

    // 뷰어 정보 제거
    this._stickerViewers.delete(viewerId);

    if (this._onViewerRemoved) {
      this._onViewerRemoved(viewerId);
    }

    return true;
  }

  /**
   * 스티커 깊이 변경
   */
  public changeStickerDepth(stickerId: string, depth: number): boolean {
    const stickerInfo = this._stickers.get(stickerId);
    if (!stickerInfo) return false;

    // 새로운 데칼 기하학 생성
    const newDecalGeometry = new DecalGeometry(
      stickerInfo.attachMesh,
      stickerInfo.position,
      stickerInfo.rotation,
      new Vector3(stickerInfo.scale.x, stickerInfo.scale.y, depth),
    );

    // 기존 기하학 교체
    stickerInfo.stickerMesh.geometry.dispose();
    stickerInfo.stickerMesh.geometry = newDecalGeometry;

    return true;
  }

  /**
   * 스티커 뷰어를 카메라 방향으로 향하게
   */
  public lookAtCamera(viewerId: string, cameraPosition: Vector3): boolean {
    const viewerInfo = this._stickerViewers.get(viewerId);
    if (!viewerInfo) return false;

    const currentRotationZ = viewerInfo.viewer.rotation.z;
    viewerInfo.viewer.lookAt(cameraPosition);
    viewerInfo.viewer.rotation.z = currentRotationZ;

    return true;
  }

  /**
   * 스케일 비율 계산
   */
  private calculateScaleRatio(viewer: Group, intersectPoint: Vector3): number {
    const viewerPosition = new Vector3();
    viewer.getWorldPosition(viewerPosition);

    // 간단한 거리 기반 스케일 계산
    const distance = viewerPosition.distanceTo(intersectPoint);
    return Math.max(0.1, Math.min(2.0, distance / 5)); // 0.1 ~ 2.0 범위로 제한
  }

  /**
   * 깊이 계산
   */
  private calculateDepth(mesh: Mesh): number {
    const boundingBox = new Box3().setFromObject(mesh);
    const size = boundingBox.getSize(new Vector3());
    return size.x / 2; // 간단한 깊이 계산
  }

  /**
   * 텍스처 로딩
   */
  private async loadTexture(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this._textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.format = THREE.RGBAFormat;
          texture.flipY = false;
          texture.anisotropy = 8;
          texture.minFilter = THREE.LinearMipMapLinearFilter;
          texture.magFilter = THREE.LinearFilter;
          resolve(texture);
        },
        undefined,
        reject,
      );
    });
  }

  /**
   * ID 생성 함수들
   */
  private generateStickerId(): string {
    return `sticker_${this._nextStickerId++}`;
  }

  private generateViewerId(): string {
    return `viewer_${this._nextViewerId++}`;
  }

  // Getter 메서드들
  public getSticker(stickerId: string): StickerInfo | undefined {
    return this._stickers.get(stickerId);
  }

  public getStickerViewer(viewerId: string): StickerViewerInfo | undefined {
    return this._stickerViewers.get(viewerId);
  }

  public getAllStickers(): Map<string, StickerInfo> {
    return this._stickers;
  }

  public getAllStickerViewers(): Map<string, StickerViewerInfo> {
    return this._stickerViewers;
  }

  public getMainStickerViewer(): Group | null {
    return this._stickerViewer;
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.clearAllStickers();

    this._stickerViewers.forEach((viewerInfo) => {
      this.removeStickerViewer(viewerInfo.id);
    });

    this._stickerViewer = null;
  }
}
